import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Fab } from '../forms/buttons';

interface LIAccordionProps {
  title: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
}

const LIAccordion: React.FC<LIAccordionProps> = ({ title, children, defaultOpen = false, className = '' }) => {
  const [open, setOpen] = useState(defaultOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const liRef = useRef<HTMLLIElement>(null);
  const [isActive, setIsActive] = useState(false);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleMouseDown = () => setIsActive(true);
  const handleMouseUp = () => setIsActive(false);

  const contentHeight = open && contentRef.current ? contentRef.current.scrollHeight : 0;

  useEffect(() => {
    if (open && liRef.current) {
      liRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [open]);

  return (
    <div className={`border-border_regular rounded-lg border`}>
      <li
        ref={liRef}
        onClick={handleToggle}
        className="py-md px-xl focus-within:ring-border_focus bg-surface_l2 hover:bg-bg_secondary_neutral active:bg-bg_secondary_bold cursor-pointer list-none rounded-lg focus-within:ring-[3px]">
        <div
          className={twMerge(
            classnames('gap-md relative flex w-full list-none items-center rounded-lg text-start'),
            className,
          )}>
          <div className="flex-1 overflow-hidden">{title}</div>

          <div className="flex flex-shrink-0 items-center">
            <Fab
              size="xs"
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              variant={open ? 'primary' : 'secondary'}
              className={!open && isActive ? 'active:bg-bg_primary' : ''}
              icon={
                <Icon
                  className={`transition-transform duration-200 ${open ? 'rotate-180' : 'rotate-0'}`}
                  color={open || isActive ? 'fg_primary_inverse' : 'fg_primary'}
                  variant={'chevron-down'}
                />
              }
            />
          </div>
        </div>
        <div
          ref={contentRef}
          style={{
            height: open ? contentHeight : 0,
            transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s',
            opacity: open ? 1 : 0,
            overflow: 'hidden',
          }}
          className={`text-fg_secondary pr-2xl text-left`}>
          {children}
        </div>
      </li>
    </div>
  );
};

export default LIAccordion;
